import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import {
  RequestListResponseSchema,
  RequestDetailResponseSchema,
} from "@/api/schemas";
import type { TimeRange } from "@/components/TimeRangeSelector";

export interface RequestFilters {
  status?: string;
  feature?: string;
  env?: string;
  user_id?: string;
  requested_model?: string;
  used_model?: string;
  cache_hit?: boolean;
}

export interface RequestPagination {
  offset: number;
  limit: number;
  sort_by: string;
  sort_dir: "asc" | "desc";
}

export function useRequests(
  tenantId: string,
  range: TimeRange,
  pagination: RequestPagination,
  filters: RequestFilters,
) {
  return useQuery({
    queryKey: [
      "requests",
      tenantId,
      range.from,
      range.to,
      pagination.offset,
      pagination.limit,
      pagination.sort_by,
      pagination.sort_dir,
      filters,
    ],
    queryFn: async () => {
      const searchParams: Record<string, string | number | boolean> = {
        from: range.from,
        to: range.to,
        offset: pagination.offset,
        limit: pagination.limit,
        sort_by: pagination.sort_by,
        sort_dir: pagination.sort_dir,
      };

      if (filters.status) searchParams.status = filters.status;
      if (filters.feature) searchParams.feature = filters.feature;
      if (filters.env) searchParams.env = filters.env;
      if (filters.user_id) searchParams.user_id = filters.user_id;
      if (filters.requested_model)
        searchParams.requested_model = filters.requested_model;
      if (filters.used_model) searchParams.used_model = filters.used_model;
      if (filters.cache_hit !== undefined)
        searchParams.cache_hit = filters.cache_hit;

      const json = await api.get("requests", { searchParams }).json();
      return RequestListResponseSchema.parse(json);
    },
    enabled: !!tenantId,
  });
}

export function useRequestDetail(requestId: string | null) {
  return useQuery({
    queryKey: ["request-detail", requestId],
    queryFn: async () => {
      const json = await api.get(`requests/${requestId}`).json();
      return RequestDetailResponseSchema.parse(json);
    },
    enabled: !!requestId,
  });
}
